// CP-143: on-state (affiliate flag ON) CTA + disclosure copy, driven by the
// CP-141b/CP-150 landing tier (affiliate_landing_tier: exact / ship / line).
//
// Only used where canBookLine()/isBookableAffiliateLink() is already true, so
// flag OFF never reaches this module and OFF output stays unchanged.
//
// Rules (CP-143 card + CJ brand rules):
// - exact: "Check price on CruiseDirect" (the link opens this sailing's search).
// - ship / line: never promise the specific sailing or date.
// - unknown / missing tier: safest wording, same as line.
// - Keep "price may differ". No official-site claim. No ® / ™.

export const PARTNER = 'CruiseDirect';
export const COMMISSION_NOTE = 'Partner link to CruiseDirect. We may earn a commission.';
export const PRICE_MAY_DIFFER = "CruiseDirect's price may differ.";

const TIER_COPY = {
  exact: {
    cta: 'Check price on CruiseDirect',
    landing: null,
  },
  ship: {
    cta: "See this ship's sailings on CruiseDirect",
    landing: "You'll pick this sailing's date on CruiseDirect.",
  },
  line: {
    cta: "See this line's sailings on CruiseDirect",
    landing: "You'll pick the ship and sailing date on CruiseDirect.",
  },
};

export function normalizeTier(tier) {
  const key = typeof tier === 'string' ? tier.trim().toLowerCase() : '';
  return Object.prototype.hasOwnProperty.call(TIER_COPY, key) ? key : 'line';
}

export function tierCtaLabel(tier) {
  return TIER_COPY[normalizeTier(tier)].cta;
}

export function tierLandingNote(tier) {
  return TIER_COPY[normalizeTier(tier)].landing;
}

// "Oct 11, 2026" in UTC, or '' when the value isn't a real date (no invented date).
export function formatCollectedDate(value) {
  if (value === null || value === undefined || value === '') return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

// AC2 sub-line. Falls back to the bare "price may differ" line when the line
// name or collected date is missing, so nothing is invented.
export function priceSubLine(line, collectedAt) {
  const name = typeof line === 'string' ? line.trim() : '';
  const date = formatCollectedDate(collectedAt);
  if (!name || !date) return PRICE_MAY_DIFFER;
  return `Price shown was last collected from ${name} on ${date}; ${PRICE_MAY_DIFFER}`;
}

// Everything a surface needs for one ON-state CTA.
export function tierCopy({ tier, line = '', collectedAt = null } = {}) {
  return {
    tier: normalizeTier(tier),
    cta: tierCtaLabel(tier),
    landing: tierLandingNote(tier),
    subLine: priceSubLine(line, collectedAt),
    commission: COMMISSION_NOTE,
  };
}

// One-line note under a deal card CTA (Hot Deals, Best Deals, Home shelves).
export function dealNote({ tier, line = '', collectedAt = null } = {}) {
  const c = tierCopy({ tier, line, collectedAt });
  return [c.landing, c.subLine, c.commission].filter(Boolean).join(' ');
}

// CP-213: the slim phone bar (56px) carries ONE line, not the three CP-154
// lines. The PO approved this exact wording (Oct 15); the full disclosure
// (landing caveat, price-may-differ, commission) stays in the price card and
// the "Learn more" link scrolls to it. Keep the wording in these constants.
// No "Featured" / "Sponsored" / "paid" wording; no (R)/(TM); no "official".
export const STICKY_DISCLOSURE_TEXT = 'Partner link \u00b7 may earn commission';
export const STICKY_DISCLOSURE_LINK_TEXT = 'Learn more';

// CP-213: the 56px bar has ~90px left for the button once the one-line disclosure
// + "Learn more" take their width at 375px, so the bar shows a short label. The
// full CP-143 label stays on the price card. Same tier rules as TIER_COPY: only
// the exact tier says "price"; ship/line never promise a sailing or date.
const STICKY_CTA = { exact: 'Check price', ship: 'See sailings', line: 'See sailings' };
export function stickyCtaLabel(tier) {
  return STICKY_CTA[normalizeTier(tier)];
}

// Id of the full disclosure block in the price card (BookNowButton rail).
export const FULL_DISCLOSURE_ID = 'booking-affiliate-desc';

// Compare rows are per line (no sailing, no collected date): catalog averages.
export const COMPARE_ON_NOTE =
  "Rates displayed are catalog averages; CruiseDirect's price may differ. Outbound booking links are sponsored affiliate links to CruiseDirect, and we may earn a commission.";
